import * as React from 'react';
import { Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils/cn';
import { formatDateTime } from '@/lib/utils/format';
import {
  MAX_FIRM_CAP_CENTS,
  formatCents,
  parseCapDollars,
  spendPosture,
  useSetFirmAiBudgetMutation,
  workClassName,
  type FirmScopeSummary,
} from '@/lib/api/ai-costs';

/**
 * Firm AI budget card on the AI cost governance screen (T-0154; T-0147,
 * amendment 01 Decision 29).
 *
 * AI work with no engagement to bill (requirement library document intake,
 * the vendor knowledge base) is charged to the firm scope, against a calendar
 * month cap with a hard stop: at the cap a new document import does not
 * start. This card shows this month's firm-scope spend against that cap
 * and lets a firm admin change the cap or go back to the default.
 *
 * Every figure is the server's (`firm_scope` of the summary); the card
 * only formats them. The meter follows the same posture bands as the
 * firm meter above it.
 */

const POSTURE_BG = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
} as const;
const POSTURE_TEXT = {
  healthy: 'text-posture-healthy',
  'at-risk': 'text-posture-at-risk',
  critical: 'text-posture-critical',
} as const;
const POSTURE_TINT = {
  healthy: 'bg-posture-healthy-tint',
  'at-risk': 'bg-posture-at-risk-tint',
  critical: 'bg-posture-critical-tint',
} as const;

export interface FirmAiBudgetCardProps {
  firmScope: FirmScopeSummary;
}

function monthLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'this month';
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function FirmAiBudgetCard({ firmScope }: FirmAiBudgetCardProps) {
  const mutation = useSetFirmAiBudgetMutation();
  // After a change, show the server's answer at once; the summary refetches behind it.
  const saved = mutation.data;
  const budget = saved ?? firmScope;

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [problem, setProblem] = React.useState<string | null>(null);
  const inputId = React.useId();

  const posture = budget.exhausted ? 'critical' : spendPosture(budget.used_cents, budget.cap_cents);
  const pct = budget.percent_of_cap != null ? budget.percent_of_cap * 100 : null;

  const startEditing = () => {
    setDraft((budget.cap_cents / 100).toFixed(2));
    setProblem(null);
    mutation.reset();
    setEditing(true);
  };

  const save = (capCents: number | null) => {
    setProblem(null);
    mutation.mutate(capCents, {
      onSuccess: () => setEditing(false),
      onError: (err) => setProblem(err.message || 'The cap could not be changed.'),
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const cents = parseCapDollars(draft);
    if (cents == null) {
      setProblem(
        `Enter an amount in dollars between $0 and ${formatCents(MAX_FIRM_CAP_CENTS)}, for example 75 or 75.50.`,
      );
      return;
    }
    save(cents);
  };

  return (
    <section
      aria-labelledby="firm-ai-budget-heading"
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 id="firm-ai-budget-heading" className="text-h3 font-semibold text-ink-900">
            Firm AI budget
          </h2>
          <p className="text-meta text-ink-500">
            AI work that belongs to no engagement: requirement library document intake, the vendor
            knowledge base, and their duplicate checks. {monthLabel(budget.period_start)}.
          </p>
        </div>
        <span
          className={cn(
            'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-meta font-medium',
            POSTURE_TINT[posture],
            POSTURE_TEXT[posture],
          )}
        >
          {budget.exhausted ? 'Used up' : pct != null ? `${pct.toFixed(1)}% used` : 'No cap set'}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-baseline gap-3">
        <span className="text-h1 font-semibold tabular-nums text-ink-900">
          {formatCents(budget.used_cents)}
        </span>
        <span className="text-body-sm text-ink-500">
          of {formatCents(budget.cap_cents)} monthly cap
          {budget.is_default ? ' (the default)' : ''} · {formatCents(budget.remaining_cents)} left ·{' '}
          {budget.txns.toLocaleString()} {budget.txns === 1 ? 'call' : 'calls'}
        </span>
      </div>

      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(100, pct ?? 100)}
        aria-label={
          pct != null ? `Firm AI budget ${pct.toFixed(1)}% used` : 'Firm AI budget: the cap is zero'
        }
        className="mt-3 h-2.5 overflow-hidden rounded-full bg-ink-100"
      >
        <div
          aria-hidden="true"
          className={cn('h-full rounded-full transition-[width]', POSTURE_BG[posture])}
          style={{ width: `${Math.min(100, pct ?? 100)}%` }}
        />
      </div>

      {budget.exhausted ? (
        <p role="status" className="mt-3 text-body-sm font-medium text-posture-critical">
          The cap is reached: new document imports do not start, and duplicate checks fall back to
          wording only, until the cap is raised or the month ends.
        </p>
      ) : null}

      {firmScope.by_work_class.length > 0 ? (
        <table className="mt-4 w-full border-collapse text-left text-body-sm">
          <caption className="sr-only">Firm-scope spend by work class this month</caption>
          <thead>
            <tr className="border-b border-ink-100 text-meta text-ink-500">
              <th scope="col" className="py-1.5 pr-2 font-medium">
                Work class
              </th>
              <th scope="col" className="px-2 py-1.5 text-right font-medium">
                Calls
              </th>
              <th scope="col" className="px-2 py-1.5 text-right font-medium">
                Avg. tokens
              </th>
              <th scope="col" className="px-2 py-1.5 text-right font-medium">
                Cost per call
              </th>
              <th scope="col" className="py-1.5 pl-2 text-right font-medium">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {firmScope.by_work_class.map((w) => (
              <tr key={w.id} className="border-b border-ink-100 last:border-b-0">
                <td className="py-1.5 pr-2">
                  <span className="inline-flex items-center rounded-chip border border-ai-surface-border bg-ai-surface px-1.5 py-0.5 text-meta font-medium text-ai-ink">
                    {w.name}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-right font-mono">{w.calls.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {w.avg_tokens.toLocaleString()}
                </td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {formatCents(w.cost_per_call_cents)}
                </td>
                <td className="py-1.5 pl-2 text-right font-mono font-medium text-ink-900">
                  {formatCents(w.total_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {firmScope.recent_expensive_calls.length > 0 ? (
        <details className="mt-3 text-body-sm text-ink-700">
          <summary className="cursor-pointer text-meta font-medium text-ink-700">
            Most expensive firm-scope calls this month (
            {firmScope.recent_expensive_calls.length.toLocaleString()})
          </summary>
          <ul className="mt-2 flex flex-col gap-1">
            {firmScope.recent_expensive_calls.slice(0, 10).map((c, i) => (
              <li key={`${c.ts}-${i}`} className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-mono text-meta text-ink-500">{formatDateTime(c.ts)}</span>
                <span>{workClassName(c.work_class)}</span>
                <span className="font-mono text-meta text-ink-500">{c.model_id}</span>
                <span className="font-mono text-meta">{(c.tokens / 1000).toFixed(1)}k tokens</span>
                <span className="ml-auto font-mono font-medium text-ink-900">
                  {formatCents(c.cost_cents)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="mt-4 border-t border-ink-100 pt-3">
        {editing ? (
          <form onSubmit={submit} className="flex flex-wrap items-end gap-2" noValidate>
            <label
              htmlFor={inputId}
              className="flex flex-col gap-1 text-meta font-medium text-ink-700"
            >
              Monthly cap in US dollars
              <Input
                id={inputId}
                inputMode="decimal"
                autoFocus
                // The whole amount is replaced more often than it is edited.
                onFocus={(e) => e.currentTarget.select()}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-invalid={problem != null}
                className="h-9 w-40 font-mono"
              />
            </label>
            <Button type="submit" size="sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save cap'}
            </Button>
            {!budget.is_default ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => save(null)}
              >
                Use the default ({formatCents(budget.default_cap_cents)})
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={mutation.isPending}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            {problem ? (
              <p role="alert" className="basis-full text-meta font-medium text-posture-critical">
                {problem}
              </p>
            ) : (
              <p className="basis-full text-meta text-ink-500">
                Takes effect at once and applies to every month. The change is recorded in the audit
                log.
              </p>
            )}
          </form>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-meta text-ink-500">
              {budget.is_default
                ? `No cap has been set, so the default of ${formatCents(budget.default_cap_cents)} a month applies.`
                : `Set by a firm admin. The default is ${formatCents(budget.default_cap_cents)} a month.`}
            </p>
            <Button type="button" size="sm" variant="outline" onClick={startEditing}>
              <Edit3 className="size-3.5" aria-hidden="true" /> Change the cap
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
